import { DateTime } from "luxon";
import { DEFAULT_TIMEZONE, ORDER_AHEAD_TIMESLOT_DIFF, removeDuplicates } from "@/utils";
import { TimeOptionMap } from "./interfaces";

export const useOrderAheadMapper = () => {
  const DATE_FORMAT = "yyyy-MM-dd";

  /**
   * @param {String} datetime ISO8601
   * @param {String} timezone
   *
   * @return {String} formatted time
   */
  const mapToTime = (datetime: string, timezone: string): string => {
    // We're formatting time based on the end date of the selected pickupTime - pickupTime + ORDER_AHEAD_TIMESLOT_DIFF
    return mapToEndTime(datetime, timezone)?.toFormat("t");
  };

  /**
   * @param {String} dateTime ISO8601
   * @param {String} timezone
   *
   * @return {DateTime | null}
   */
  const mapToEndTime = (dateTime: string, timezone: string = DEFAULT_TIMEZONE) => {
    return DateTime.fromISO(dateTime).setZone(timezone)?.plus({ minutes: ORDER_AHEAD_TIMESLOT_DIFF });
  };

  /**
   * @param {String} datetime ISO8601
   * @param {String} timezone
   * @param {DateTime | null} currentTime "now" as optional argument
   *
   * @return {String} formatted date
   */
  const mapToFormattedDate = (datetime: string, timezone: string = DEFAULT_TIMEZONE, currentTime: DateTime = null): string => {
    const now = currentTime ?? DateTime.now().setZone(timezone);
    const today = DateTime.fromISO(now).toFormat(DATE_FORMAT);
    const tomorrow = DateTime.fromISO(now).plus({ days: 1 }).toFormat(DATE_FORMAT);

    const selTimestamp = DateTime.fromISO(datetime).setZone(timezone);
    const selDate = selTimestamp.toFormat(DATE_FORMAT);

    if (selDate === today) {
      return "Today";
    }

    if (selDate === tomorrow) {
      return "Tomorrow";
    }

    return selTimestamp.toFormat("EEE, DD");
  };

  /**
   * Map Pickup times into select "option" with named days
   *
   * @param {String[]} pickupTimes ISO8601[]
   * @param {String} timezone
   * @param {DateTime | null} currentTime "now" as optional argument
   *
   * @return {{id: String, name: String}[]} array of objects for select
   */
  const mapOptionsByNamedDays = (
    pickupTimes: string[],
    timezone: string = DEFAULT_TIMEZONE,
    currentTime: DateTime = null
  ): TimeOptionMap => {
    if (!pickupTimes.length) {
      return [{ name: "NA" }];
    }

    const namedDays = pickupTimes.map((pickupTime) => {
      return {
        id: DateTime.fromISO(pickupTime).setZone(timezone).toFormat(DATE_FORMAT),
        name: mapToFormattedDate(pickupTime, timezone, currentTime)
      };
    });

    return removeDuplicates(namedDays, (item) => item.id);
  };

  /**
   * Filter available time options by selected date
   *
   * @param {String} selectedDate yyyy-MM-dd
   * @param {String[]} pickupTimes ISO8601[]
   * @param {String} timezone
   *
   * @return {{id: String, name: String}[]} array of objects for select
   */
  const mapTimeOptionsByDate = (
    selectedDate: string,
    pickupTimes: string[],
    timezone: string = DEFAULT_TIMEZONE
  ): TimeOptionMap => {
    return pickupTimes
      .filter((pickupTime) => selectedDate === DateTime.fromISO(pickupTime).setZone(timezone).toFormat(DATE_FORMAT))
      .map((pickupTime) => ({
        id: pickupTime,
        name: mapToTime(pickupTime, timezone)
      }));
  };

  const mapToEstimatedOrderTime = (datetime: string, timezone: string): string => {
    const currentSlot = DateTime.fromISO(datetime).setZone(timezone).startOf("minute");
    const now = DateTime.now().setZone(timezone).endOf("minute");
    // Ensuring we won't display negative numbers if difference is in the past
    const minutesOfDiff = Math.max(0, parseInt(currentSlot.diff(now, ["minutes"]).toObject().minutes));

    const currentSlotEndTime = mapToEndTime(currentSlot.toString(), timezone);

    if (minutesOfDiff < 60) {
      return `by ${currentSlotEndTime.toFormat("h:mma")}`;
    }

    return `Today at ${currentSlotEndTime.toFormat("t")}`;
  };

  return {
    mapToTime,
    mapToEndTime,
    mapToFormattedDate,
    mapOptionsByNamedDays,
    mapTimeOptionsByDate,
    mapToEstimatedOrderTime
  };
};
